/**
 * 登录 / 注册。
 *
 * 后端流程（见 spec/06-api-contract.md）：
 *   auth.code.request { account }              → 服务端存验证码
 *   auth.login        { account, code, agreement } → { user, session:{userId,token,expiresAt} }
 *
 * 同一个账号既是登录也是注册：账号不存在时后端自动建号（isNewUser 标记）。
 *
 * 注意：勾选协议时必须回传 termsVersion / privacyVersion，后端会写进会话和审计日志，
 * 这是「用户已同意某版本协议」的合规留痕，不能省。
 */
import { useEffect, useState } from "react";
import { callApi, setSession, type Session } from "../api";
import { site } from "../data/site";

/** 协议版本号，与合规页文案同步更新；改协议内容时必须同时改这里。 */
const AGREEMENT_VERSIONS = { terms: "2026-06-28", privacy: "2026-06-28" };

/**
 * 是否显示手机/邮箱验证码登录。
 *
 * 目前只留微信扫码登录：短信通道未接入，后端演示验证码后门也已关闭
 * （AUTH_DEMO_LOGIN 默认关），所以验证码登录无法送达也无法校验。
 * 等接入腾讯云短信、后端 sms.enabled 为 true 后，把这里改回 true 即可恢复。
 */
const ENABLE_CODE_LOGIN = false;

type Step = "account" | "code";

type Capabilities = {
  sms?: { enabled?: boolean; mode?: string };
  email?: { enabled?: boolean; mode?: string };
  wechat?: { webEnabled?: boolean; miniEnabled?: boolean; mode?: string };
};

export function AuthPage({ onNavigate }: { onNavigate: (route: string) => void }) {
  const [step, setStep] = useState<Step>("account");
  const [account, setAccount] = useState("");
  const [code, setCode] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [capabilities, setCapabilities] = useState<Capabilities | null>(null);

  useEffect(() => {
    callApi<Capabilities>("auth.capabilities").then((result) => {
      if (result.ok) setCapabilities(result);
    });
  }, []);

  const channel = account.includes("@") ? "邮箱" : "手机号";

  async function wechatLogin() {
    setBusy(true);
    setError("");
    const result = await callApi<{ authorizeUrl?: string }>("auth.wechat.authorize", {
      returnTo: "records",
    });
    setBusy(false);
    if (!result.ok || !result.authorizeUrl) {
      setError(result.ok ? "未取得微信授权地址。" : result.error);
      return;
    }
    // 跳到微信扫码页，扫完微信会带 code/state 回到 #auth/wechat
    window.location.href = result.authorizeUrl;
  }

  async function requestCode(event: React.FormEvent) {
    event.preventDefault();
    if (!account.trim()) {
      setError("请输入手机号或邮箱。");
      return;
    }
    setBusy(true);
    setError("");
    const result = await callApi<{ account?: string; note?: string; demoCode?: string }>(
      "auth.code.request",
      { account: account.trim() },
    );
    setBusy(false);
    if (!result.ok) {
      setError(result.error);
      return;
    }
    setNotice(
      [`验证码已发往 ${result.account ?? account}`, result.note].filter(Boolean).join("　"),
    );
    setStep("code");
  }

  async function submitLogin(event: React.FormEvent) {
    event.preventDefault();
    if (!code.trim()) {
      setError("请输入验证码。");
      return;
    }
    if (!agreed) {
      setError("请先阅读并同意用户协议与隐私政策。");
      return;
    }
    setBusy(true);
    setError("");
    const result = await callApi<{ session?: Session; user?: { name?: string; isNewUser?: boolean } }>(
      "auth.login",
      {
        account: account.trim(),
        code: code.trim(),
        agreement: {
          accepted: true,
          acceptedAt: new Date().toISOString(),
          termsVersion: AGREEMENT_VERSIONS.terms,
          privacyVersion: AGREEMENT_VERSIONS.privacy,
          source: "web-auth",
        },
      },
    );
    setBusy(false);
    if (!result.ok) {
      setError(result.error);
      return;
    }
    if (!result.session?.token) {
      setError("登录返回缺少会话信息，请重试。");
      return;
    }
    setSession(result.session);
    onNavigate("records");
  }

  return (
    <main className="page auth-page">
      <p className="console-kicker">Account Sign In</p>
      <h1>登录 / 注册滕云子账号</h1>
      <p className="auth-subtitle">
        使用微信扫码进入账号，账号用于保存排盘记录、学习笔记与会员状态。
      </p>

      {capabilities?.wechat?.webEnabled && (
        <div className="auth-primary">
          <button type="button" className="wechat-login" onClick={wechatLogin} disabled={busy}>
            微信扫码登录 / 注册
          </button>
        </div>
      )}

      {capabilities && !capabilities.wechat?.webEnabled && !ENABLE_CODE_LOGIN && (
        <p className="auth-hint">登录服务维护中，请稍后再试，或通过 {site.email} 联系我们。</p>
      )}

      {ENABLE_CODE_LOGIN && step === "account" && (
        <form className="auth-form" onSubmit={requestCode}>
          <label>
            手机号或邮箱
            <input
              type="text"
              value={account}
              onChange={(e) => setAccount(e.target.value)}
              placeholder="请输入手机号或邮箱"
              autoComplete="username"
              required
            />
          </label>
          <button type="submit" disabled={busy}>
            {busy ? "发送中…" : `获取${channel}验证码`}
          </button>
        </form>
      )}

      {ENABLE_CODE_LOGIN && step === "code" && (
        <form className="auth-form" onSubmit={submitLogin}>
          {notice && <p className="auth-notice">{notice}</p>}
          <label>
            验证码
            <input
              type="text"
              inputMode="numeric"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="请输入收到的验证码"
              autoComplete="one-time-code"
              required
            />
          </label>
          <label className="auth-agreement">
            <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />
            <span>
              我已年满十八周岁，并阅读同意
              <button type="button" className="link" onClick={() => onNavigate("legal/terms")}>
                《用户协议》
              </button>
              与
              <button type="button" className="link" onClick={() => onNavigate("legal/privacy")}>
                《隐私政策》
              </button>
            </span>
          </label>
          <div className="auth-actions">
            <button type="submit" disabled={busy}>
              {busy ? "登录中…" : "登录"}
            </button>
            <button type="button" className="link" onClick={() => setStep("account")}>
              换个账号
            </button>
          </div>
        </form>
      )}

      {error && <p className="form-error">{error}</p>}

      {ENABLE_CODE_LOGIN && capabilities?.sms?.enabled === false && (
        <p className="auth-hint">
          短信通道尚未开通，如未收到验证码请通过 {site.email} 联系客服。
        </p>
      )}
    </main>
  );
}
